import { useState } from "react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtKM = (n) =>
    new Intl.NumberFormat("bs-BA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0) + " KM";

export default function PricingAdmin({ pricing, reload }) {
    return (
        <div className="space-y-8">
            <Section
                title="Cjenovnik ograda"
                subtitle="Cijena ograde po m² + cijene kapija po m² za svaki model (sa PDV-om)"
                items={pricing.models}
                endpoint="models"
                fields={[
                    { key: "price_per_m2", label: "Ograda/m²", suffix: "KM" },
                    { key: "gate_pjesacka_per_m2", label: "Pješačka/m²", suffix: "KM" },
                    { key: "gate_dvokrilna_per_m2", label: "Dvokrilna/m²", suffix: "KM" },
                    { key: "gate_klizna_per_m2", label: "Klizna/m²", suffix: "KM" },
                ]}
                reload={reload}
                testidPrefix="model"
            />
            <Section
                title="Kapije — parametri"
                subtitle="Minimalna m² i nadoplata po tipu (cijena po m² je definisana po modelu iznad)"
                items={pricing.gates}
                endpoint="gates"
                fields={[
                    { key: "min_area_m2", label: "Min. m²", suffix: "m²" },
                    { key: "surcharge_name", label: "Nadoplata", type: "text" },
                    { key: "surcharge_price", label: "Iznos", suffix: "KM" },
                ]}
                reload={reload}
                testidPrefix="gate"
            />
            <Section
                title="Elektro motori"
                subtitle="Cijena motora + opcija uključenja"
                items={pricing.motors}
                endpoint="motors"
                fields={[
                    { key: "price", label: "Cijena", suffix: "KM" },
                    { key: "enabled", label: "Dostupno u ponudi", type: "bool" },
                ]}
                reload={reload}
                testidPrefix="motor"
            />
            <Section
                title="Pribor"
                subtitle="Dodatni elementi (sa PDV-om)"
                items={pricing.accessories}
                endpoint="accessories"
                fields={[{ key: "price", label: "Cijena", suffix: "KM" }]}
                reload={reload}
                testidPrefix="acc"
            />
        </div>
    );
}

function Section({ title, subtitle, items, endpoint, fields, reload, testidPrefix }) {
    return (
        <div className="bg-white border border-[hsl(var(--line))]">
            <div className="p-6 border-b border-[hsl(var(--line))]">
                <h2 className="text-lg font-bold">{title}</h2>
                {subtitle && <p className="text-xs text-[hsl(var(--muted-ink))] mt-1">{subtitle}</p>}
            </div>
            <div className="divide-y divide-[hsl(var(--line))]">
                {items.map(it => (
                    <Row
                        key={it.id}
                        item={it}
                        endpoint={endpoint}
                        fields={fields}
                        reload={reload}
                        testidPrefix={testidPrefix}
                    />
                ))}
            </div>
        </div>
    );
}

function Row({ item, endpoint, fields, reload, testidPrefix }) {
    const [edited, setEdited] = useState({ ...item });
    const [saving, setSaving] = useState(false);
    const dirty = fields.some(f => edited[f.key] !== item[f.key]);

    const save = async () => {
        setSaving(true);
        try {
            const r = await fetch(`${API}/api/pricing/${endpoint}/${item.id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify(edited),
            });
            if (!r.ok) throw new Error(await r.text());
            toast.success("Cijena sačuvana");
            reload();
        } catch (e) {
            toast.error("Greška pri spremanju");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-5 flex items-center gap-4 flex-wrap" data-testid={`row-${testidPrefix}-${item.id}`}>
            <div className="font-medium min-w-[180px] flex-1">{item.name}</div>
            {fields.map(f => (
                <div key={f.key} className="flex items-center gap-2">
                    {f.type === "bool" ? (
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                checked={!!edited[f.key]}
                                onChange={e => setEdited({ ...edited, [f.key]: e.target.checked })}
                                data-testid={`input-${testidPrefix}-${item.id}-${f.key}`}
                                className="w-4 h-4 accent-[hsl(var(--accent))]"
                            />
                            <span className="text-xs text-[hsl(var(--muted-ink))]">{f.label}</span>
                        </label>
                    ) : f.type === "text" ? (
                        <>
                            <div className="text-xs text-[hsl(var(--muted-ink))] hidden sm:block">{f.label}</div>
                            <input
                                type="text"
                                value={edited[f.key] ?? ""}
                                onChange={e => setEdited({ ...edited, [f.key]: e.target.value })}
                                data-testid={`input-${testidPrefix}-${item.id}-${f.key}`}
                                className="form-input w-40"
                                placeholder="opciono"
                            />
                        </>
                    ) : (
                        <>
                            <div className="text-xs text-[hsl(var(--muted-ink))] hidden sm:block">{f.label}</div>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={edited[f.key]}
                                onChange={e => setEdited({ ...edited, [f.key]: parseFloat(e.target.value) || 0 })}
                                data-testid={`input-${testidPrefix}-${item.id}-${f.key}`}
                                className="form-input w-32 text-right"
                            />
                            <span className="text-xs text-[hsl(var(--muted-ink))]">{f.suffix}</span>
                        </>
                    )}
                </div>
            ))}
            <button
                onClick={save}
                disabled={!dirty || saving}
                data-testid={`save-${testidPrefix}-${item.id}`}
                className="px-4 py-2 bg-[hsl(var(--ink))] text-white text-xs font-medium hover:bg-[hsl(var(--accent))] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
                {saving ? "..." : dirty ? "Sačuvaj" : "Sačuvano"}
            </button>
        </div>
    );
}
